import React, { useState, useEffect } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react";
import axios from "../lib/AxiosGo";
import { toast } from "react-toastify";
import { useAppSettings } from "../context/AppSettingsContext";

const SettingsPage = () => {
  const { settings, getMediaUrl, refreshSettings } = useAppSettings();

  const [formData, setFormData] = useState({
    app_name: "",
    app_description: "",
  });

  const [files, setFiles] = useState({
    logo: null,
    logo_light: null,
    logo_icon: null,
    favicon: null,
  });

  const [previews, setPreviews] = useState({
    logo: "",
    logo_light: "",
    logo_icon: "",
    favicon: "",
  });

  const [resetFlags, setResetFlags] = useState({
    reset_logo: false,
    reset_logo_light: false,
    reset_logo_icon: false,
    reset_favicon: false,
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData({
        app_name: settings.app_name || "Layanan Pengaduan Polda Jateng",
        app_description: settings.app_description || "Layanan Bot WhatsApp Pengaduan Masyarakat",
      });

      setPreviews({
        logo: getMediaUrl(settings.logo),
        logo_light: getMediaUrl(settings.logo_light),
        logo_icon: getMediaUrl(settings.logo_icon),
        favicon: getMediaUrl(settings.favicon),
      });

      setResetFlags({
        reset_logo: false,
        reset_logo_light: false,
        reset_logo_icon: false,
        reset_favicon: false,
      });

      setFiles({
        logo: null,
        logo_light: null,
        logo_icon: null,
        favicon: null,
      });
    }
  }, [settings, getMediaUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.warning("Ukuran file maksimal 5MB");
        return;
      }

      setFiles((prev) => ({ ...prev, [field]: file }));
      setPreviews((prev) => ({ ...prev, [field]: URL.createObjectURL(file) }));
      setResetFlags((prev) => ({ ...prev, [`reset_${field}`]: false }));
    }
  };

  const handleResetField = (field, defaultPath) => {
    setFiles((prev) => ({ ...prev, [field]: null }));
    setPreviews((prev) => ({ ...prev, [field]: getMediaUrl(defaultPath) }));
    setResetFlags((prev) => ({ ...prev, [`reset_${field}`]: true }));
    toast.info(`${field.replace('_', ' ')} akan dikembalikan ke default saat disimpan.`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = new FormData();
      data.append("app_name", formData.app_name);
      data.append("app_description", formData.app_description);

      if (files.logo) data.append("logo", files.logo);
      if (files.logo_light) data.append("logo_light", files.logo_light);
      if (files.logo_icon) data.append("logo_icon", files.logo_icon);
      if (files.favicon) data.append("favicon", files.favicon);

      if (resetFlags.reset_logo) data.append("reset_logo", "true");
      if (resetFlags.reset_logo_light) data.append("reset_logo_light", "true");
      if (resetFlags.reset_logo_icon) data.append("reset_logo_icon", "true");
      if (resetFlags.reset_favicon) data.append("reset_favicon", "true");

      const res = await axios.post("/settings/update", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data?.success) {
        toast.success(res.data.message || "Pengaturan berhasil diperbarui!");
        await refreshSettings();
      } else {
        toast.error(res.data?.message || "Gagal memperbarui pengaturan");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      const msg = error.response?.data?.message || "Terjadi kesalahan saat menyimpan pengaturan";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Pengaturan Aplikasi" />

      {/* Header Banner */}
      <div className="card radius-12 border-0 shadow-sm mb-24 overflow-hidden">
        <div className="card-body p-24 bg-gradient-primary text-white d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h4 className="text-white mb-8 d-flex align-items-center gap-2">
              <Icon icon="solar:settings-bold-duotone" className="text-3xl" />
              Pengaturan Aplikasi & Branding
            </h4>
            <p className="text-white-50 mb-0">
              Kustomisasi identitas aplikasi seperti Logo Utama, Logo Light/Dark, Favicon tab browser, dan Nama Aplikasi.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="btn btn-warning px-24 py-12 radius-8 fw-semibold d-flex align-items-center gap-2 shadow"
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <Icon icon="solar:disk-bold" className="text-xl" />
                <span>Simpan Perubahan</span>
              </>
            )}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row g-4">
          {/* Sisi Kiri: Upload Logo & Favicon */}
          <div className="col-lg-8">
            {/* Card 1: Pengaturan Logo */}
            <div className="card radius-12 border-0 shadow-sm mb-24">
              <div className="card-header py-16 px-24 bg-base border border-end-0 border-start-0 border-top-0 d-flex align-items-center gap-2">
                <Icon icon="solar:gallery-bold-duotone" className="text-primary-600 text-2xl" />
                <h6 className="text-lg fw-semibold mb-0">Logo Aplikasi</h6>
              </div>
              <div className="card-body p-24">
                <div className="row g-4">
                  {/* 1. Logo Light Mode */}
                  <div className="col-md-6">
                    <div className="p-16 border radius-12 bg-neutral-50 h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="d-flex align-items-center justify-content-between mb-12">
                          <label className="form-label fw-semibold text-primary-light mb-0">
                            Logo Light Mode (Header & Sidebar Terang)
                          </label>
                          <button
                            type="button"
                            onClick={() => handleResetField("logo", "assets/images/logo.png")}
                            className="btn btn-sm btn-outline-secondary py-2 px-8 radius-6 text-xs d-flex align-items-center gap-1"
                            title="Kembalikan ke logo default"
                          >
                            <Icon icon="solar:restart-outline" /> Reset
                          </button>
                        </div>
                        <p className="text-xs text-secondary-light mb-16">
                          Digunakan pada tampilan latar terang dan halaman login. Format PNG/SVG transparan direkomendasikan.
                        </p>
                        <div
                          className="d-flex align-items-center justify-content-center p-16 rounded-3 mb-16 border border-dashed bg-white"
                          style={{ minHeight: "100px" }}
                        >
                          {previews.logo ? (
                            <img
                              src={previews.logo}
                              alt="Preview Logo Light"
                              style={{ maxHeight: "60px", maxWidth: "100%", objectFit: "contain" }}
                            />
                          ) : (
                            <span className="text-muted text-sm">Tidak ada logo</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <input
                          type="file"
                          id="logoInput"
                          className="form-control form-control-sm"
                          accept="image/*"
                          onChange={(e) => handleFileChange("logo", e)}
                        />
                        <span className="text-xs text-muted mt-4 d-block">Maksimal 5MB (PNG, JPG, SVG, WEBP)</span>
                      </div>
                    </div>
                  </div>

                  {/* 2. Logo Dark Mode */}
                  <div className="col-md-6">
                    <div className="p-16 border radius-12 bg-neutral-50 h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="d-flex align-items-center justify-content-between mb-12">
                          <label className="form-label fw-semibold text-primary-light mb-0">
                            Logo Dark Mode (Sidebar Gelap)
                          </label>
                          <button
                            type="button"
                            onClick={() => handleResetField("logo_light", "assets/images/logo-light.png")}
                            className="btn btn-sm btn-outline-secondary py-2 px-8 radius-6 text-xs d-flex align-items-center gap-1"
                            title="Kembalikan ke logo default"
                          >
                            <Icon icon="solar:restart-outline" /> Reset
                          </button>
                        </div>
                        <p className="text-xs text-secondary-light mb-16">
                          Digunakan saat tema sidebar gelap aktif. Gunakan warna terang / putih transparan.
                        </p>
                        <div
                          className="d-flex align-items-center justify-content-center p-16 rounded-3 mb-16 border border-dashed"
                          style={{ minHeight: "100px", backgroundColor: "#1e2837" }}
                        >
                          {previews.logo_light ? (
                            <img
                              src={previews.logo_light}
                              alt="Preview Logo Dark"
                              style={{ maxHeight: "60px", maxWidth: "100%", objectFit: "contain" }}
                            />
                          ) : (
                            <span className="text-muted text-sm">Tidak ada logo</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <input
                          type="file"
                          id="logoLightInput"
                          className="form-control form-control-sm"
                          accept="image/*"
                          onChange={(e) => handleFileChange("logo_light", e)}
                        />
                        <span className="text-xs text-muted mt-4 d-block">Maksimal 5MB (PNG, JPG, SVG, WEBP)</span>
                      </div>
                    </div>
                  </div>

                  {/* 3. Logo Mini / Icon */}
                  <div className="col-md-6">
                    <div className="p-16 border radius-12 bg-neutral-50 h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="d-flex align-items-center justify-content-between mb-12">
                          <label className="form-label fw-semibold text-primary-light mb-0">
                            Logo Mini / Icon Sidebar Collapsed
                          </label>
                          <button
                            type="button"
                            onClick={() => handleResetField("logo_icon", "assets/images/logo-icon.png")}
                            className="btn btn-sm btn-outline-secondary py-2 px-8 radius-6 text-xs d-flex align-items-center gap-1"
                            title="Kembalikan ke icon default"
                          >
                            <Icon icon="solar:restart-outline" /> Reset
                          </button>
                        </div>
                        <p className="text-xs text-secondary-light mb-16">
                          Tampil saat sidebar disembunyikan/collapse. Rasio aspek 1:1 (persegi).
                        </p>
                        <div
                          className="d-flex align-items-center justify-content-center p-16 rounded-3 mb-16 border border-dashed bg-white"
                          style={{ minHeight: "90px" }}
                        >
                          {previews.logo_icon ? (
                            <img
                              src={previews.logo_icon}
                              alt="Preview Logo Icon"
                              style={{ height: "45px", width: "45px", objectFit: "contain" }}
                            />
                          ) : (
                            <span className="text-muted text-sm">Tidak ada icon</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <input
                          type="file"
                          id="logoIconInput"
                          className="form-control form-control-sm"
                          accept="image/*"
                          onChange={(e) => handleFileChange("logo_icon", e)}
                        />
                        <span className="text-xs text-muted mt-4 d-block">Ukuran ideal: 48x48 px atau 64x64 px</span>
                      </div>
                    </div>
                  </div>

                  {/* 4. Favicon */}
                  <div className="col-md-6">
                    <div className="p-16 border radius-12 bg-neutral-50 h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="d-flex align-items-center justify-content-between mb-12">
                          <label className="form-label fw-semibold text-primary-light mb-0">
                            Favicon Tab Browser
                          </label>
                          <button
                            type="button"
                            onClick={() => handleResetField("favicon", "assets/images/favicon.png")}
                            className="btn btn-sm btn-outline-secondary py-2 px-8 radius-6 text-xs d-flex align-items-center gap-1"
                            title="Kembalikan ke favicon default"
                          >
                            <Icon icon="solar:restart-outline" /> Reset
                          </button>
                        </div>
                        <p className="text-xs text-secondary-light mb-16">
                          Ikon kecil pada tab peramban/browser website.
                        </p>
                        <div
                          className="d-flex align-items-center justify-content-center p-16 rounded-3 mb-16 border border-dashed bg-white"
                          style={{ minHeight: "90px" }}
                        >
                          {previews.favicon ? (
                            <div className="d-flex align-items-center gap-3">
                              <img
                                src={previews.favicon}
                                alt="Preview Favicon"
                                style={{ height: "32px", width: "32px", objectFit: "contain" }}
                              />
                              <span className="text-xs fw-medium text-secondary-light">
                                Ukuran 32x32 / 16x16 px
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted text-sm">Tidak ada favicon</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <input
                          type="file"
                          id="faviconInput"
                          className="form-control form-control-sm"
                          accept="image/*,.ico"
                          onChange={(e) => handleFileChange("favicon", e)}
                        />
                        <span className="text-xs text-muted mt-4 d-block">Format: ICO, PNG, SVG (Maksimal 2MB)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Informasi Aplikasi */}
            <div className="card radius-12 border-0 shadow-sm">
              <div className="card-header py-16 px-24 bg-base border border-end-0 border-start-0 border-top-0 d-flex align-items-center gap-2">
                <Icon icon="solar:info-circle-bold-duotone" className="text-primary-600 text-2xl" />
                <h6 className="text-lg fw-semibold mb-0">Informasi Umum Aplikasi</h6>
              </div>
              <div className="card-body p-24">
                <div className="mb-20">
                  <label className="form-label fw-semibold text-primary-light">
                    Nama Aplikasi / Title Sistem
                  </label>
                  <input
                    type="text"
                    name="app_name"
                    value={formData.app_name}
                    onChange={handleInputChange}
                    placeholder="Contoh: Layanan Pengaduan Polda Jateng"
                    className="form-control radius-8"
                  />
                  <span className="text-xs text-secondary-light mt-4 d-block">
                    Nama ini akan muncul pada judul tab browser dan sidebar dashboard.
                  </span>
                </div>

                <div>
                  <label className="form-label fw-semibold text-primary-light">
                    Deskripsi / Tagline Aplikasi
                  </label>
                  <textarea
                    name="app_description"
                    rows="3"
                    value={formData.app_description}
                    onChange={handleInputChange}
                    placeholder="Contoh: Layanan Bot WhatsApp Pengaduan Masyarakat Polda Jateng"
                    className="form-control radius-8"
                  />
                  <span className="text-xs text-secondary-light mt-4 d-block">
                    Teks deskripsi sistem pengaduan.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sisi Kanan: Real-time Live Preview Mockup */}
          <div className="col-lg-4">
            <div className="card radius-12 border-0 shadow-sm sticky-top" style={{ top: "90px" }}>
              <div className="card-header py-16 px-24 bg-base border border-end-0 border-start-0 border-top-0 d-flex align-items-center gap-2">
                <Icon icon="solar:eye-bold-duotone" className="text-primary-600 text-2xl" />
                <h6 className="text-lg fw-semibold mb-0">Simulasi Tampilan Langsung</h6>
              </div>
              <div className="card-body p-24">
                {/* 1. Mockup Tab Browser */}
                <label className="text-xs fw-bold text-uppercase text-secondary-light tracking-wider mb-8 d-block">
                  Tab Browser Chrome
                </label>
                <div className="browser-mockup mb-24 rounded-3 border overflow-hidden shadow-sm">
                  <div className="bg-light p-8 border-bottom d-flex align-items-center gap-2">
                    <div className="d-flex gap-1 me-2">
                      <span className="d-inline-block rounded-circle bg-danger" style={{ width: "9px", height: "9px" }}></span>
                      <span className="d-inline-block rounded-circle bg-warning" style={{ width: "9px", height: "9px" }}></span>
                      <span className="d-inline-block rounded-circle bg-success" style={{ width: "9px", height: "9px" }}></span>
                    </div>
                    {/* Simulated active tab */}
                    <div className="bg-white px-12 py-6 rounded-top border d-flex align-items-center gap-2 shadow-sm" style={{ maxWidth: "220px" }}>
                      {previews.favicon ? (
                        <img
                          src={previews.favicon}
                          alt="favicon"
                          style={{ width: "16px", height: "16px", objectFit: "contain" }}
                        />
                      ) : (
                        <Icon icon="solar:globe-outline" className="text-muted" />
                      )}
                      <span className="text-xs text-truncate fw-medium" style={{ maxWidth: "160px" }}>
                        {formData.app_name || "Dashboard"}
                      </span>
                    </div>
                  </div>
                  <div className="p-16 bg-white text-center">
                    <p className="text-xs text-muted mb-0">Favicon & Nama tab browser akan disinkronkan secara otomatis.</p>
                  </div>
                </div>

                {/* 2. Mockup Sidebar Brand */}
                <label className="text-xs fw-bold text-uppercase text-secondary-light tracking-wider mb-8 d-block">
                  Tampilan Header Sidebar
                </label>
                <div className="p-16 rounded-3 mb-16 text-center border" style={{ backgroundColor: "#1e2837" }}>
                  <span className="text-xs text-white-50 d-block mb-8">Sidebar Gelap (Dark Mode):</span>
                  <div className="d-flex justify-content-center align-items-center py-8">
                    {previews.logo_light ? (
                      <img
                        src={previews.logo_light}
                        alt="Sidebar Dark Logo"
                        style={{ maxHeight: "40px", maxWidth: "180px", objectFit: "contain" }}
                      />
                    ) : (
                      <span className="text-white-50 text-xs">Belum ada logo</span>
                    )}
                  </div>
                </div>

                <div className="p-16 rounded-3 mb-24 text-center border bg-white">
                  <span className="text-xs text-muted d-block mb-8">Sidebar Terang (Light Mode):</span>
                  <div className="d-flex justify-content-center align-items-center py-8">
                    {previews.logo ? (
                      <img
                        src={previews.logo}
                        alt="Sidebar Light Logo"
                        style={{ maxHeight: "40px", maxWidth: "180px", objectFit: "contain" }}
                      />
                    ) : (
                      <span className="text-muted text-xs">Belum ada logo</span>
                    )}
                  </div>
                </div>

                {/* Tombol Simpan Action */}
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary py-12 radius-8 fw-semibold d-flex align-items-center justify-content-center gap-2"
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span>Menyimpan Perubahan...</span>
                      </>
                    ) : (
                      <>
                        <Icon icon="solar:check-read-bold" className="text-xl" />
                        <span>Simpan Semua Pengaturan</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </MasterLayout>
  );
};

export default SettingsPage;
